// speech.ts — thin wrappers over the browser Web Speech API.
//
// Two halves, both 100% client-side (no key, no network):
//   • TTS  — SpeechSynthesis. We pick the most energetic voice on the device and
//            speak Miko's lines with a lively rate/pitch.
//   • STT  — SpeechRecognition (webkit-prefixed on Chrome/Edge). One-shot listen
//            that resolves with the recognised transcript.
//
// The DOM lib doesn't type SpeechRecognition, so we declare the slim shapes we
// use here rather than pulling a polyfill.

/* ── minimal SpeechRecognition typings ─────────────────────────────────────*/
interface SR extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SREvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}
interface SREvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}
type SRCtor = new () => SR;

function srCtor(): SRCtor | null {
  const w = window as unknown as { SpeechRecognition?: SRCtor; webkitSpeechRecognition?: SRCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Is voice input available on this device/browser? (TTS is far more universal.) */
export const speechRecognitionSupported = (): boolean => srCtor() !== null;
export const speechSynthesisSupported = (): boolean =>
  typeof window !== 'undefined' && 'speechSynthesis' in window;

/* ── voice picking ─────────────────────────────────────────────────────────*/

// Voices load asynchronously and INCREMENTALLY — on Edge/Chrome the good
// neural "Natural"/network voices arrive after the robotic local ones. A
// persistent voiceschanged listener keeps the cache fresh, and we re-pick the
// best voice on every speak (the list is tiny) instead of locking in whatever
// happened to be loaded first. That lock-in was exactly how Miko ended up
// stuck on a robotic Windows SAPI voice.
let voiceCache: SpeechSynthesisVoice[] = [];
let listening = false;
function refreshVoices(): SpeechSynthesisVoice[] {
  if (!speechSynthesisSupported()) return [];
  if (!listening) {
    listening = true;
    window.speechSynthesis.addEventListener('voiceschanged', () => {
      voiceCache = window.speechSynthesis.getVoices();
    });
  }
  const now = window.speechSynthesis.getVoices();
  if (now.length) voiceCache = now;
  return voiceCache;
}
function voicesReady(): Promise<SpeechSynthesisVoice[]> {
  const now = refreshVoices();
  if (now.length) return Promise.resolve(now);
  // First call before any voices exist: give the browser a beat to load them.
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(refreshVoices()), 800);
    window.speechSynthesis.addEventListener(
      'voiceschanged',
      () => {
        clearTimeout(t);
        resolve(refreshVoices());
      },
      { once: true },
    );
  });
}

// Kid-friendly ranking. The gold standard is Edge/Windows 11's neural voices —
// "Ana" and "Maisie" are literal child voices; Aria/Jenny/Sonia/Libby are warm
// and natural. Chrome's "Google …" network voices are the next tier. The
// classic robotic SAPI voices (David/Mark/Zira "Desktop") rank at the bottom.
const KID_VOICES: Array<[RegExp, number]> = [
  [/\bana\b/, 45], // MS neural CHILD voice (en-US)
  [/maisie/, 42], // MS neural child voice (en-GB)
  [/natural/, 34], // any MS "… Online (Natural)" neural voice
  [/aria/, 26],
  [/jenny/, 24],
  [/sonia/, 22],
  [/libby/, 20],
  [/michelle/, 18],
  [/emma|ava\b/, 16],
  [/google/, 20], // Chrome network voices — decent, way above SAPI
  [/samantha/, 14], // macOS/iOS default — warm
  [/nicky|flo\b|eddy/, 10],
];
const ROBOTIC: Array<[RegExp, number]> = [
  [/david|mark\b/, -30], // classic Windows SAPI male — THE robot voice
  [/zira/, -18], // classic SAPI female — still flat
  [/desktop/, -12],
  [/compact/, -30],
  [/espeak/, -40],
];

function scoreVoice(v: SpeechSynthesisVoice): number {
  const name = v.name.toLowerCase();
  const lang = v.lang.toLowerCase();
  let score = 0;
  if (lang.startsWith('en')) score += 40; // Miko speaks English
  if (lang.startsWith('en-us')) score += 4;
  if (lang.startsWith('en-gb')) score += 2;
  if (!v.localService) score += 8; // network voices are richer
  for (const [re, pts] of KID_VOICES) if (re.test(name)) score += pts;
  for (const [re, pts] of ROBOTIC) if (re.test(name)) score += pts;
  return score;
}

/** True for neural/network voices that already sound human — pitch-shifting
 *  those makes them WORSE (warbly), so speak() leaves them alone. */
export function isNaturalVoice(v: SpeechSynthesisVoice): boolean {
  return /natural|neural|online|google|premium|enhanced/i.test(v.name) || !v.localService;
}

let lastLogged = '';
/** Pick the most kid-friendly voice currently available (re-ranked each call —
 *  the browser's voice list grows as network voices load). */
export async function kidFriendlyVoice(): Promise<SpeechSynthesisVoice | null> {
  const voices = await voicesReady();
  if (!voices.length) return null;
  const best = [...voices].sort((a, b) => scoreVoice(b) - scoreVoice(a))[0] ?? null;
  if (best && best.name !== lastLogged) {
    lastLogged = best.name;
    console.info(`[miko-voice] using: ${best.name} (${best.lang})`);
  }
  return best;
}

/** @deprecated old name — kept so nothing breaks if referenced elsewhere. */
export const energeticVoice = kidFriendlyVoice;

/* ── speaking ──────────────────────────────────────────────────────────────*/

export type SpeakHandlers = { onStart?: () => void; onEnd?: () => void };

/** Speak a line aloud in Miko's kid-friendly voice. Cancels any in-flight
 *  speech first so lines never overlap. Resolves when speech ends (or
 *  immediately if synthesis is unavailable). */
export async function speak(text: string, h: SpeakHandlers = {}): Promise<void> {
  if (!speechSynthesisSupported() || !text.trim()) {
    h.onEnd?.();
    return;
  }
  const synth = window.speechSynthesis;
  synth.cancel(); // never stack utterances
  const u = new SpeechSynthesisUtterance(text);
  const v = await kidFriendlyVoice();
  if (v) u.voice = v;
  if (v && isNaturalVoice(v)) {
    // Neural/network voices already sound human — pitch-shifting them adds a
    // warble that reads as "AI voice". Leave them natural, just lively pace.
    u.rate = 1.04;
    u.pitch = 1.0;
  } else {
    // Legacy local voices are deep + flat; a gentle lift makes them friendlier
    // without going chipmunk.
    u.rate = 1.05;
    u.pitch = 1.12;
  }
  u.volume = 1;
  return new Promise((resolve) => {
    u.onstart = () => h.onStart?.();
    u.onend = () => {
      h.onEnd?.();
      resolve();
    };
    u.onerror = () => {
      h.onEnd?.();
      resolve();
    };
    synth.speak(u);
  });
}

/** Stop any speech immediately. */
export function stopSpeaking(): void {
  if (speechSynthesisSupported()) window.speechSynthesis.cancel();
}

/* ── listening ─────────────────────────────────────────────────────────────*/

export type Listener = {
  stop: () => void;
};

/** Start a one-shot listen. Calls onResult with the final transcript, or onError
 *  with a short reason. Returns a handle to stop early. Returns null if STT is
 *  unavailable. */
export function listen(cbs: {
  onResult: (transcript: string) => void;
  onError?: (reason: string) => void;
  onEnd?: () => void;
}): Listener | null {
  const Ctor = srCtor();
  if (!Ctor) {
    cbs.onError?.('unsupported');
    return null;
  }
  const rec = new Ctor();
  rec.lang = 'en-US';
  rec.continuous = false;
  rec.interimResults = false;
  rec.maxAlternatives = 1;

  let got = false;
  rec.onresult = (e) => {
    const first = e.results?.[0]?.[0]?.transcript ?? '';
    if (first.trim()) {
      got = true;
      cbs.onResult(first.trim());
    }
  };
  rec.onerror = (e) => cbs.onError?.(e.error || 'error');
  rec.onend = () => {
    if (!got) cbs.onError?.('no-speech');
    cbs.onEnd?.();
  };

  try {
    rec.start();
  } catch {
    // start() throws if already running — treat as a no-op.
  }
  return { stop: () => rec.stop() };
}
